import { Controller } from "@hotwired/stimulus"
import { customTooltips } from "@coreui/chartjs"
import { getStyle } from "@coreui/utils"
import Chart from "chart.js/auto"

const months = ["January", "February", "March", "April", "May", "June", "July"]
const weekdays = ["M", "T", "W", "T", "F", "S", "S"]

export default class extends Controller {
  connect() {
    this.charts = []
    this.configureChartDefaults()
    this.createCardCharts()
    this.createSparklineCharts()
    this.createSocialCharts()

    this.updateCharts = this.updateCharts.bind(this)
    document.documentElement.addEventListener("ColorSchemeChange", this.updateCharts)
    this.updateCharts()
  }

  disconnect() {
    document.documentElement.removeEventListener("ColorSchemeChange", this.updateCharts)
    this.charts.forEach((chart) => chart.destroy())
    this.charts = []
  }

  configureChartDefaults() {
    Chart.defaults.elements.point.hitRadius = 1
    Chart.defaults.plugins.tooltip.enabled = false
    Chart.defaults.plugins.tooltip.mode = "index"
    Chart.defaults.plugins.tooltip.position = "nearest"
    Chart.defaults.plugins.tooltip.external = customTooltips
    Chart.defaults.color = getStyle("--cui-body-color")
  }

  createCardCharts() {
    this.cardChart1 = this.createChart("card-chart1", {
      type: "line",
      data: {
        labels: months,
        datasets: [
          {
            label: "My First dataset",
            backgroundColor: "transparent",
            borderColor: "rgba(255,255,255,.55)",
            pointBackgroundColor: getStyle("--cui-primary"),
            data: [65, 59, 84, 84, 51, 55, 40],
          },
        ],
      },
      options: this.cardLineOptions(30, 89, true),
    })

    this.cardChart2 = this.createChart("card-chart2", {
      type: "line",
      data: {
        labels: months,
        datasets: [
          {
            label: "My First dataset",
            backgroundColor: "transparent",
            borderColor: "rgba(255,255,255,.55)",
            pointBackgroundColor: getStyle("--cui-info"),
            data: [1, 18, 9, 17, 34, 22, 11],
          },
        ],
      },
      options: this.cardLineOptions(-9, 39),
    })

    this.createChart("card-chart3", {
      type: "line",
      data: {
        labels: months,
        datasets: [
          {
            label: "My First dataset",
            backgroundColor: "rgba(255,255,255,.2)",
            borderColor: "rgba(255,255,255,.55)",
            data: [78, 81, 80, 45, 34, 12, 40],
            fill: true,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { x: { display: false }, y: { display: false } },
        elements: {
          line: { borderWidth: 2, tension: 0.4 },
          point: { radius: 0, hitRadius: 10, hoverRadius: 4 },
        },
      },
    })

    this.createChart("card-chart4", {
      type: "bar",
      data: {
        labels: [
          ...months,
          "August",
          "September",
          "October",
          "November",
          "December",
          "January",
          "February",
          "March",
          "April",
        ],
        datasets: [
          {
            label: "My First dataset",
            backgroundColor: "rgba(255,255,255,.2)",
            borderColor: "rgba(255,255,255,.55)",
            data: [78, 81, 80, 45, 34, 12, 40, 85, 65, 23, 12, 98, 34, 84, 67, 82],
            barPercentage: 0.6,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false, drawTicks: false }, ticks: { display: false } },
          y: { border: { display: false }, grid: { display: false, drawTicks: false }, ticks: { display: false } },
        },
      },
    })
  }

  cardLineOptions(min, max, tension = false) {
    return {
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: {
          border: { display: false },
          grid: { display: false },
          ticks: { display: false },
        },
        y: {
          min,
          max,
          display: false,
          grid: { display: false },
          ticks: { display: false },
        },
      },
      elements: {
        line: { borderWidth: 1, ...(tension ? { tension: 0.4 } : {}) },
        point: { radius: 4, hitRadius: 10, hoverRadius: 4 },
      },
    }
  }

  createSparklineCharts() {
    const barColors = ["--cui-primary", "--cui-warning", "--cui-success"]
    barColors.forEach((color, index) => {
      this.createChart(`sparkline-chart-${index + 1}`, {
        type: "bar",
        data: {
          labels: [...weekdays, ...weekdays, "M"],
          datasets: [
            {
              backgroundColor: getStyle(color),
              borderColor: "transparent",
              borderWidth: 1,
              data: Array.from({ length: 15 }, () => this.random(40, 100)),
            },
          ],
        },
        options: this.sparklineOptions(),
      })
    })

    const lineColors = ["--cui-info", "--cui-success", "--cui-danger"]
    lineColors.forEach((color, index) => {
      this.createChart(`sparkline-chart-${index + 4}`, {
        type: "line",
        data: {
          labels: weekdays,
          datasets: [
            {
              backgroundColor: "transparent",
              borderColor: getStyle(color),
              borderWidth: 2,
              data: Array.from({ length: 7 }, () => this.random(40, 100)),
            },
          ],
        },
        options: this.sparklineOptions({
          line: { tension: 0.4 },
          point: { radius: 0 },
        }),
      })
    })
  }

  sparklineOptions(elements) {
    return {
      maintainAspectRatio: false,
      ...(elements ? { elements } : {}),
      plugins: { legend: { display: false } },
      scales: { x: { display: false }, y: { display: false } },
    }
  }

  createSocialCharts() {
    const datasets = [
      [65, 59, 84, 84, 51, 55, 40],
      [1, 13, 9, 17, 34, 41, 38],
      [78, 81, 80, 45, 34, 12, 40],
    ]

    datasets.forEach((data, index) => {
      this.createChart(`social-box-chart-${index + 1}`, {
        type: "line",
        data: {
          labels: months,
          datasets: [
            {
              backgroundColor: "rgba(255,255,255,.1)",
              borderColor: "rgba(255,255,255,.55)",
              pointHoverBackgroundColor: "#fff",
              borderWidth: 2,
              data,
              fill: true,
            },
          ],
        },
        options: {
          elements: {
            line: { tension: 0.4 },
            point: { radius: 0, hitRadius: 10, hoverRadius: 4, hoverBorderWidth: 3 },
          },
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { x: { display: false }, y: { display: false } },
        },
      })
    })
  }

  createChart(id, configuration) {
    const canvas = this.element.querySelector(`#${id}`)
    if (!canvas) return

    Chart.getChart(canvas)?.destroy()
    const chart = new Chart(canvas, configuration)
    this.charts.push(chart)
    return chart
  }

  updateCharts() {
    Chart.defaults.color = getStyle("--cui-body-color")

    if (this.cardChart1) {
      this.cardChart1.data.datasets[0].pointBackgroundColor = getStyle("--cui-primary")
      this.cardChart1.update()
    }

    if (this.cardChart2) {
      this.cardChart2.data.datasets[0].pointBackgroundColor = getStyle("--cui-info")
      this.cardChart2.update()
    }
  }

  random(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min)
  }
}
